import { RoutingJobResponse, RoutingJobViewResponse } from '../api/routingJobsApi';
import { OrderBundle, VerifyPayload } from '../api/wmService';
import { TaskInfo } from '../models/types';
import { adaptReworldTaskInfo, adaptReworldVerifyPayload, fetchReworldVerifyPayload, mapVerifyOrdersFromView } from './reworld/viewAdapters';
import { adaptJumboBuyTaskInfo, adaptJumboBuyVerifyPayload, fetchJumboBuyVerifyPayload, mapJumboBuyOrders } from './jumbobuy/viewAdapters';
import { adaptGembaTaskInfo, adaptGembaVerifyPayload, fetchGembaVerifyPayload, mapGembaOrdersFromView } from './gemba/viewAdapters';

export interface FileRequirement {
  id: string;
  label: string;
}

export interface AdaptTaskArgs {
  depotId: string;
  jobId: string;
  summaryView: RoutingJobViewResponse;
  tripsView: RoutingJobViewResponse;
  supportViews?: Record<string, RoutingJobViewResponse | undefined>;
}

export interface TenantModelDefinition {
  key: string;
  requiredFiles: FileRequirement[];
  adaptTaskInfo: (args: AdaptTaskArgs) => TaskInfo;
  mapOrdersFromView?: (items: AnyRecord[]) => OrderBundle[];
  ordersViewId?: string;
  fetchVerifyPayload?: (depotId: string, jobId: string) => Promise<VerifyPayload>;
  adaptVerifyPayloadFromViews?: (
    job: RoutingJobResponse,
    views: Record<string, RoutingJobViewResponse | undefined>
  ) => VerifyPayload;
}

type AnyRecord = Record<string, any>;

export interface ModelTypeOption {
  value: string;
  label: string;
}

const MODEL_TYPE_LABELS: Record<string, string> = {
  REWORLD_WST_MGT: 'ReWorld Waste Management',
  SLOTTED: 'Slotted',
  SID: 'SID',
  PRY: 'PRY',
  GEMBA_SLOTTED: 'GEMBA Slotted',
};

export function getModelTypeLabel(modelType: string): string {
  const normalized = modelType.trim().toUpperCase();
  if (MODEL_TYPE_LABELS[normalized]) {
    return MODEL_TYPE_LABELS[normalized];
  }
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

const definitions: Record<string, TenantModelDefinition> = {
  'reworld:reworld_wst_mgt': {
    key: 'reworld:reworld_wst_mgt',
    requiredFiles: [
      { id: 'orders', label: 'Orders CSV' },
      { id: 'depots', label: 'Depot CSV' },
      { id: 'drivers', label: 'Drivers CSV' },
      { id: 'driver_schedule', label: 'Driver Schedule CSV' },
      { id: 'vehicles', label: 'Vehicles CSV' },
      { id: 'trailers', label: 'Trailers CSV' },
      { id: 'generator_windows', label: 'Generator Windows CSV' },
      { id: 'actuals', label: 'Actuals CSV' },
    ],
    adaptTaskInfo: ({ depotId, jobId, summaryView, tripsView }) =>
      adaptReworldTaskInfo(depotId, jobId, summaryView, tripsView),
    mapOrdersFromView: mapVerifyOrdersFromView,
    ordersViewId: 'verify_orders',
    fetchVerifyPayload: fetchReworldVerifyPayload,
    adaptVerifyPayloadFromViews: adaptReworldVerifyPayload,
  },
  'jumbobuy:slotted': {
    key: 'jumbobuy:slotted',
    requiredFiles: [
      { id: 'buyers', label: 'Buyers CSV' },
      { id: 'drivers', label: 'Drivers CSV' },
    ],
    adaptTaskInfo: ({ depotId, jobId, summaryView, tripsView }) =>
      adaptJumboBuyTaskInfo(depotId, jobId, summaryView, tripsView),
    mapOrdersFromView: mapJumboBuyOrders,
    fetchVerifyPayload: fetchJumboBuyVerifyPayload,
    adaptVerifyPayloadFromViews: adaptJumboBuyVerifyPayload,
  },
  'jumbobuy:sid': {
    key: 'jumbobuy:sid',
    requiredFiles: [
      { id: 'buyers', label: 'Buyers CSV' },
      { id: 'drivers', label: 'Drivers CSV' },
    ],
    adaptTaskInfo: ({ depotId, jobId, summaryView, tripsView }) =>
      adaptJumboBuyTaskInfo(depotId, jobId, summaryView, tripsView),
    mapOrdersFromView: mapJumboBuyOrders,
    fetchVerifyPayload: fetchJumboBuyVerifyPayload,
    adaptVerifyPayloadFromViews: adaptJumboBuyVerifyPayload,
  },
  'jumbobuy:pry': {
    key: 'jumbobuy:pry',
    requiredFiles: [
      { id: 'buyers', label: 'Buyers CSV' },
    ],
    adaptTaskInfo: ({ depotId, jobId, summaryView, tripsView }) =>
      adaptJumboBuyTaskInfo(depotId, jobId, summaryView, tripsView),
    mapOrdersFromView: mapJumboBuyOrders,
    fetchVerifyPayload: fetchJumboBuyVerifyPayload,
    adaptVerifyPayloadFromViews: adaptJumboBuyVerifyPayload,
  },
  'gemba:gemba_slotted': {
    key: 'gemba:gemba_slotted',
    requiredFiles: [
      { id: 'orders', label: 'Orders CSV' },
      { id: 'vehicles', label: 'Vehicles CSV' },
      { id: 'depot', label: 'Depot CSV' },
    ],
    adaptTaskInfo: ({ depotId, jobId, summaryView, tripsView, supportViews }) =>
      adaptGembaTaskInfo(depotId, jobId, summaryView, tripsView, supportViews),
    mapOrdersFromView: mapGembaOrdersFromView,
    ordersViewId: 'verify_orders',
    fetchVerifyPayload: fetchGembaVerifyPayload,
    adaptVerifyPayloadFromViews: adaptGembaVerifyPayload,
  },
};

const defaultDefinition = definitions['reworld:reworld_wst_mgt'];

export function getTenantModelDefinition(tenantId?: string, modelType?: string): TenantModelDefinition {
  if (!tenantId || !modelType) {
    return defaultDefinition;
  }
  const key = `${tenantId.toLowerCase()}:${modelType.toLowerCase()}`;
  return definitions[key] ?? defaultDefinition;
}

export function getTenantModelTypeOptions(tenantId?: string): ModelTypeOption[] {
  if (!tenantId) {
    return [];
  }
  const prefix = `${tenantId.trim().toLowerCase()}:`;
  return Object.values(definitions)
    .filter((definition) => definition.key.startsWith(prefix))
    .map((definition) => {
      const modelType = definition.key.slice(prefix.length).toUpperCase();
      return {
        value: modelType,
        label: getModelTypeLabel(modelType),
      };
    });
}
